<script setup lang="ts">
const queryClient = useQueryClient();
await queryClient.ensureQueryData(newsListQueryOptions());
const { data: newsData } = useNewsList();
const { entrance: contactEntrance } = useContactPage();
const facebookUrl = computed(() => getFacebookUrl(contactEntrance.value));
useSeo({ title: 'Nyheder' });

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: 'Forside', item: '/' },
      { name: 'Nyheder' },
    ],
  }),
]);

// Prefetch article authors in parallel
const authorIds = [...new Set(
  newsData.value?.items
    ?.map(item => item.body[0]?.value?.author)
    .filter((id): id is number => !!id) ?? [],
)];
if (authorIds.length) {
  await Promise.all(
    authorIds.map(id => queryClient.ensureQueryData(authorQueryOptions(id))),
  );
}

const articles = computed(() => newsData.value?.items ?? []);
const dateFormatter = new Intl.DateTimeFormat('da-DK', { dateStyle: 'long' });
</script>

<template>
  <div>
    <section class="py-16 md:py-24">
      <div class="max-w-6xl mx-auto px-6">
        <h1 class="text-3xl md:text-4xl font-bold">
          Nyheder
        </h1>
        <p class="mt-2 text-[var(--color-charcoal)]/60">
          Seneste nyt fra Kronborgs Køreskole
        </p>

        <!-- Facebook CTA Banner -->
        <div
          v-if="facebookUrl"
          class="mt-8 p-6 rounded-xl bg-gradient-to-r from-[var(--color-peach)]/10 to-primary-50 border-l-4 border-[var(--color-peach)] flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <UIcon
            name="i-simple-icons-facebook"
            class="size-8 text-[#1877F2] shrink-0"
          />
          <div class="flex-1">
            <h3 class="font-semibold text-[var(--color-charcoal)]">
              Få de seneste opdateringer på Facebook
            </h3>
            <p class="text-sm text-[var(--color-charcoal)]/70 mt-1">
              Succeshistorier, tips og daglige opdateringer — følg os og gå ikke glip af noget.
            </p>
          </div>
          <UButton
            :to="facebookUrl"
            target="_blank"
            rel="noopener noreferrer"
            label="Følg os på Facebook"
            trailing-icon="i-heroicons-arrow-right"
            color="primary"
            size="sm"
            class="shrink-0"
          />
        </div>

        <div
          v-if="!articles.length"
          class="mt-8 text-[var(--color-charcoal)]/70"
        >
          Ingen nyheder lige nu — se vores Facebook for de seneste opdateringer.
        </div>

        <div class="mt-10 space-y-6">
          <NewsArticle
            v-for="article in articles"
            :key="article.id"
            :page="article"
            :date-formatter="dateFormatter"
          />
        </div>
      </div>
    </section>
  </div>
</template>
